import React, { createContext, useContext, useEffect, useState } from "react";
import defaultContent from "./defaultContent";
import { fetchContent } from "./api";

function mergeContent(remote) {
  if (!remote || typeof remote !== "object") return defaultContent;
  return {
    ...defaultContent,
    ...remote,
    footer: {
      ...defaultContent.footer,
      ...(remote.footer || {}),
      columns: remote.footer?.columns || defaultContent.footer.columns,
      legalPages: {
        ...defaultContent.footer.legalPages,
        ...(remote.footer?.legalPages || {}),
        privacy: {
          ...defaultContent.footer.legalPages.privacy,
          ...(remote.footer?.legalPages?.privacy || {}),
        },
        terms: {
          ...defaultContent.footer.legalPages.terms,
          ...(remote.footer?.legalPages?.terms || {}),
        },
      },
    },
    nav: {
      ...defaultContent.nav,
      ...(remote.nav || {}),
    },
  };
}

/** CMS payload inlined in spa.html by Laravel — available before any JS fetch. */
function readBootContent() {
  if (typeof document === "undefined") return null;
  const el = document.getElementById("cms-boot");
  if (!el) return null;
  const raw = el.textContent;
  if (!raw || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

const bootRemote = readBootContent();
const initialContent = bootRemote ? mergeContent(bootRemote) : defaultContent;

const ContentContext = createContext({
  content: defaultContent,
  loading: true,
  error: null,
  refresh: () => {},
});

export function ContentProvider({ children }) {
  const [content, setContent] = useState(initialContent);
  const [loading, setLoading] = useState(!bootRemote);
  const [error, setError] = useState(null);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchContent();
      // null = static mode / no CMS — keep bundled defaults without erroring
      setContent(data ? mergeContent(data) : defaultContent);
      setError(null);
    } catch (err) {
      // Keep whatever is already on screen (boot JSON or bundled defaults)
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Boot JSON already painted LCP copy — skip the chained /api/content request.
    if (bootRemote) return undefined;
    load();
    return undefined;
  }, [load]);

  useEffect(() => {
    if (content?.site?.seoTitle) {
      document.title = content.site.seoTitle;
    }
    if (content?.site?.seoDescription) {
      let meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", content.site.seoDescription);
    }
  }, [content]);

  return (
    <ContentContext.Provider value={{ content, loading, error, refresh: load }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext);
}

export default ContentContext;
